import axios from 'axios';

import { formatDate } from '../utils/formUtils';

const DAY_IN_MICRO_SECONDS = 24 * 60 * 60 * 1000;

const getAPIUrl = () => {
  if (process.env.REACT_APP_STAGE === 'dev') {
    return process.env.REACT_APP_API_URL;
  }

  return `${process.env.REACT_APP_API_ROOT}${process.env.REACT_APP_API_PATH}`;
};

const createApiClient = () => {
  const instance = axios.create({
    baseURL: getAPIUrl(),
    headers: { 'Content-Type': 'application/json;charset=UTF-8' },
  });

  return instance;
};

const api = createApiClient();

const throwStatusError = (error) => {
  const status = error?.response?.status;

  if (status) {
    throw new Error(`Failed with status: ${status}`);
  }

  throw error;
};

const ensureJsonResponse = (response) => {
  const contentType = response?.headers?.['content-type'] || '';
  if (contentType.includes('application/json')) return response.data;

  throw new Error('The server did not return a JSON response.');
};

export const postClientData = async (clientData) => {
  try {
    const response = await api.post('/client', clientData);
    return ensureJsonResponse(response);
  } catch (error) {
    throwStatusError(error);
  }
};

export const updateClientData = async (id, clientData) => {
  try {
    const response = await api.put(`/client/${id}`, clientData);
    return ensureJsonResponse(response);
  } catch (error) {
    throwStatusError(error);
  }
};

export const confirmOption = async (data) => {
  try {
    const response = await api.post('/contracting/confirm', transformConfirmationData(data));
    return ensureJsonResponse(response);
  } catch (error) {
    throwStatusError(error);
  }
};

export const simulateCredit = async (clientData) => {
  const simulationData = transformSimulationData(clientData);

  try {
    const response = await api.post('/contracting/simulate', simulationData);
    return ensureJsonResponse(response);
  } catch (error) {
    throwStatusError(error);
  }
};

const transformSimulationData = (data) => {
  const currentYear = new Date().getFullYear();

  return {
    update_id: data?.uuId || null,
    client: {
      email: data.email,
      rut: data.rut + (data.dv ? data.dv : ''),
      employer_rut: '',
      name: data.name,
      surnames: data.surnames,
      lastname: data.surnames?.split(' ')[0] || '',
      second_lastname: data.surnames?.split(' ').slice(1).join(' ') || '',
      nationality: 'CHILE',
      mobile: data.phone,
      landline1: '',
      landline2: '',
      salary: data.salary || 0,
      worker_type: data.workerType || 'DEP',
      job_start_date: formatDate(data.startWorkingDate),
      fono: '',
      ctacte: '',
      comment: '',
      hadHeard: '',
      whereHeard: '',
      birth_date: '',
      activity: '',
      region: '',
      commune: '',
      campaign: '',
      purchase_intention: '',
      consent_checked: data?.consentChecked || false,
      client_id: document.cookie
        .match(/_ga=(.+?);/)[1]
        .split('.')
        .slice(-2)
        .join('.'),
      session_id: getSessionId(),
    },
    credit: {
      type_financing: data.typeFinancing,
      type: data.type ?? data.option?.type ?? null,
      amount_financed: data.amountFinanced || data.carValue - data.footAmount,
      base_amount: data.footAmount,
      executive: data?.executiveRut ?? process.env.REACT_APP_EXECUTIVE_RUT,
      fees: data.fee || '36',
      ...(data.paidFees && { paid_fees: data.paidFees }),
      ...(data.monthlyInstallmentValue && {
        monthly_installment_value: data.monthlyInstallmentValue,
      }),
      financial_entity: data.entidad,
      financial_product: '',
      start_credit_date: formatDate(new Date(Date.now())),
      first_due_date: formatDate(new Date(Date.now() + DAY_IN_MICRO_SECONDS * 31)),
      id_product: null,
      portability: '',
      product_type: data.option?.product_type ?? '',
      promotion: '',
      rate: null,
      rate_discount: null,
      sales_person: data?.sellerRut ?? process.env.REACT_APP_SELLER_RUT,
      segment: 'PA',
      flexible_ci: 'NO',
      unemployment_insurance_option: 'NO',
      life_insurance_option: 'SI',
      variable_installments: 'NO',
    },
    source: {
      branch: data?.storeCode ?? process.env.REACT_APP_STORE_CODE,
      channel: 'CK',
      channel_request: data?.channelRequest ?? `${data.typeFinancing}_v2`,
      dealer: data?.dealerRut ?? process.env.REACT_APP_DEALER_RUT,
      name: data?.sourceName ?? 'Crediautos',
    },
    vehicle: {
      brand: data.brand || 'Mercedes Benz',
      class: 'AU',
      model: data.model || 'CLA 250',
      price: data.carValue || data.amountFinanced || data.carValue - data.footAmount,
      condition: data.caryear && data.caryear >= currentYear ? 'Nuevo' : 'Usado',
      use: 'PA',
      type: data.caryear && data.caryear >= currentYear ? 'NU' : 'US',
      year: data.caryear || '',
      registration_number: data?.registrationNumber ?? '',
    },
    others: {
      evaluation_flow: true,
      operation_number: 0,
      request_type: '',
    },
  };
};

const transformConfirmationData = (data) => {
  const currentYear = new Date().getFullYear();
  return {
    client: {
      email: data.email,
      rut: data.rut + (data.dv ? data.dv : ''),
      name: data.name,
      surnames: data.surnames,
      lastname: data.surnames?.split(' ')[0] || '',
      second_lastname: data.surnames?.split(' ').slice(1).join(' ') || '',
      mobile: data.phone?.replace(/\+/g, ''),
      nationality: 'CHILE',
      employer_rut: null,
      salary: null,
      landline1: null,
      landline2: null,
      client_id: document.cookie
        .match(/_ga=(.+?);/)[1]
        .split('.')
        .slice(-2)
        .join('.'),
      session_id: getSessionId(),
    },
    credit: {
      type_financing: data.typeFinancing,
      type: data.type ?? data.option?.type ?? null,
      fees: data.fee || 36,
      amount_financed:
        data.amountFinancedCalculated || data.amountFinanced || data.carValue - data.footAmount,
      start_credit_date: formatDate(new Date(Date.now())),
      financial_entity: data.entidad,
      first_due_date: formatDate(new Date(Date.now() + DAY_IN_MICRO_SECONDS * 31)),
      rate_discount: null,
      rate: data.option?.rate || null,
      id_product: data.option?.product_id ?? '9',
      segment: 'PA',
      variable_installments: null,
      dealer_rut: process.env.REACT_APP_DEALER_RUT,
      seller_rut: data?.sellerRut ?? process.env.REACT_APP_SELLER_RUT,
      executive_rut: data?.executiveRut ?? process.env.REACT_APP_EXECUTIVE_RUT,
      store_code: data?.storeCode ?? process.env.REACT_APP_STORE_CODE,
      surcharges: null,
    },
    vehicle: {
      price: data.carValue || data.amountFinanced || data.carValue - data.footAmount,
      condition: data.caryear && data.caryear >= currentYear ? 'Nuevo' : 'Usado',
      use: 'PA',
      type: data.caryear && data.caryear >= currentYear ? 'NU' : 'US',
      year: data.caryear || '',
      brand: data.brand || 'Mercedes Benz',
      class: 'AU',
      model: data.model || 'CLA 250',
      registration_number: data?.registrationNumber ?? '',
    },
    source: {
      channel: 'CK',
    },
    simulationResponse: {
      id: data.simulationId ?? '',
      totalFinance: data.option?.totalFinancing ?? 0,
      installmentValue: data.option?.installmentValue ?? 0,
      installmentValue2: data.option?.installmentValue2 ?? 0,
      promissoryNoteTax: data.option?.promissoryNoteTax ?? 0,
      totalSurcharges: data.option?.totalSurcharges ?? 0,
      discountPercentage: 0,
      lifeInsuranceAmount: data.option?.lifeInsuranceAmount ?? 0,
      unemploymentInsuranceAmount: data.option?.unemploymentInsuranceAmount ?? 0,
    },
  };
};

const getSessionId = () => {
  const gaMCPHL42NFFCookie = document.cookie.match(/_ga_MCPHL42NFF=([^;]+)/);
  let session_id = null;

  if (gaMCPHL42NFFCookie) {
    const cookieValue = gaMCPHL42NFFCookie[1];
    const parts = cookieValue.split('.');
    if (parts.length >= 3) {
      session_id = parts[2];
      session_id = session_id.split('$')[0].match(/[a-zA-Z](\d+)/)?.[1];
    }
  }

  return session_id;
};
